import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AUTH_ENDPOINTS } from "../config/constants";

const FirstTimeLogin = ({ user, onComplete }) => {
  const [username, setUsername] = useState(user?.username || "");
  const [hasAcceptedGuidelines, setHasAcceptedGuidelines] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, check if we have a returnTo path and ensure it's preserved
  useEffect(() => {
    const returnTo =
      sessionStorage.getItem("preLoginPath") ||
      localStorage.getItem("preLoginPath");
    if (returnTo) {
      // Re-save to session to ensure it persists through the setup
      sessionStorage.setItem("preLoginPath", returnTo);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    if (!hasAcceptedGuidelines) {
      setError("You must accept the community guidelines to continue");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch(AUTH_ENDPOINTS.COMPLETE_FIRST_LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          username,
          hasAcceptedGuidelines: true,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.msg || "Failed to complete setup");
      }

      onComplete();

      // Try all possible sources for the return URL in order of priority
      const redirectTo =
        data.redirectTo ||
        sessionStorage.getItem("preLoginPath") ||
        localStorage.getItem("preLoginPath") ||
        "/";

      // Clean up storage
      sessionStorage.removeItem("preLoginPath");
      localStorage.removeItem("preLoginPath");

      console.log("Redirecting to:", redirectTo);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
      <h2 className="text-2xl font-bold mb-6 dark:text-gray-100">
        Welcome to G80 Or Not!
      </h2>
      <p className="mb-4 text-gray-600 dark:text-gray-400">
        Please complete your profile setup to continue.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded dark:bg-red-900 dark:text-red-100">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Choose your username
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            required
            minLength={3}
            maxLength={30}
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Username can only be changed once. Choose carefully!
          </p>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="guidelines"
              type="checkbox"
              checked={hasAcceptedGuidelines}
              onChange={(e) => setHasAcceptedGuidelines(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded dark:border-gray-700 dark:bg-gray-800 dark:checked:bg-blue-600"
            />
          </div>
          <div className="ml-3">
            <label
              htmlFor="guidelines"
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              I have read and agree to the{" "}
              <Link
                to="/community-guidelines"
                target="_blank"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
              >
                Community Guidelines
              </Link>
            </label>
          </div>
        </div>
        <button
          type="submit"
          disabled={!hasAcceptedGuidelines || !username}
          className="w-full py-2 px-4 rounded transition-all duration-300 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 dark:from-primary-600 dark:to-primary-700 dark:hover:from-primary-700 dark:hover:to-primary-800 dark:active:from-primary-800 dark:active:to-primary-900"
        >
          Complete Setup
        </button>
      </form>
    </div>
  );
};

export default FirstTimeLogin;
